import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Loader2, Gift, Users, Mail, Phone, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import AnimatedLogo from "../components/AnimatedLogo";
import api from "../lib/api";

const registerSchema = z.object({
  name: z.string().min(2, "Nom trop court"),
  email: z.string().email("Email invalide"),
  phone: z.string().optional(),
  password: z.string().min(6, "Minimum 6 caractères"),
  confirmPassword: z.string(),
  accept_marketing: z.boolean().default(false),
  accept_terms: z.boolean().refine(val => val === true, "Vous devez accepter les conditions")
}).refine(data => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"]
});

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});

export default function MemberAuth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const registerForm = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      accept_marketing: false,
      accept_terms: false
    }
  });

  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const onRegister = async (data) => {
    setLoading(true);
    try {
      const res = await api.post("/member-portal/register", {
        name: data.name,
        email: data.email,
        phone: data.phone,
        password: data.password,
        accept_marketing: data.accept_marketing
      });
      
      localStorage.setItem("tireo_member_token", res.data.token);
      localStorage.setItem("tireo_member", JSON.stringify(res.data.member));
      
      toast.success("Compte créé avec succès !");
      navigate("/play");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async (data) => {
    setLoading(true);
    try {
      const res = await api.post("/member-portal/login", data);
      
      localStorage.setItem("tireo_member_token", res.data.token);
      localStorage.setItem("tireo_member", JSON.stringify(res.data.member));
      
      toast.success("Connexion réussie !");
      navigate("/play");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -top-48 -left-48" />
        <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -bottom-48 -right-48" />
        <Gift className="absolute top-20 right-20 w-24 h-24 text-white/10" />
        <Gift className="absolute bottom-20 left-20 w-16 h-16 text-white/10 rotate-12" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-4 sm:p-6">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <AnimatedLogo linkTo="/" showAnimation={true} size="default" variant="light" />
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/20 text-sm sm:text-base px-2 sm:px-4">
                <span className="hidden sm:inline">Espace </span>Organisateur
                <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
              </Button>
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            {/* Hero Text */}
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-4xl font-bold text-white mb-2 sm:mb-3" style={{ fontFamily: 'Outfit' }}>
                {mode === "login" ? "Bon retour !" : "Rejoignez l'aventure !"}
              </h1>
              <p className="text-white/80 text-sm sm:text-base">
                {mode === "login" 
                  ? "Connectez-vous pour participer aux jeux"
                  : "Inscrivez-vous et participez à des tirages exclusifs"
                }
              </p>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl" style={{ fontFamily: 'Outfit' }}>
                  {mode === "login" ? "Connexion Participant" : "Créer un compte"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mode === "login" ? (
                  <div className="space-y-4">
                    <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-3.5 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="votre@email.com"
                          className="pl-10 h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          {...loginForm.register("email")}
                        />
                      </div>
                      {loginForm.formState.errors.email && (
                        <p className="text-sm text-destructive">{loginForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500 pr-10"
                          {...loginForm.register("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3.5 text-muted-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-11 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
                    </Button>
                    </form>
                  </div>
                ) : (
                  <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet *</Label>
                      <div className="relative">
                        <User className="w-4 h-4 absolute left-3 top-3.5 text-muted-foreground" />
                        <Input
                          id="name"
                          placeholder="Jean Dupont"
                          className="pl-10 h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          {...registerForm.register("name")}
                        />
                      </div>
                      {registerForm.formState.errors.name && (
                        <p className="text-sm text-destructive">{registerForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email *</Label>
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-3.5 text-muted-foreground" />
                        <Input
                          id="reg-email"
                          type="email"
                          placeholder="votre@email.com"
                          className="pl-10 h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          {...registerForm.register("email")}
                        />
                      </div>
                      {registerForm.formState.errors.email && (
                        <p className="text-sm text-destructive">{registerForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <div className="relative">
                        <Phone className="w-4 h-4 absolute left-3 top-3.5 text-muted-foreground" />
                        <Input
                          id="phone"
                          placeholder="06 12 34 56 78"
                          className="pl-10 h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          {...registerForm.register("phone")}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="reg-password">Mot de passe *</Label>
                        <Input
                          id="reg-password"
                          type="password"
                          placeholder="••••••••"
                          className="h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          {...registerForm.register("password")}
                        />
                        {registerForm.formState.errors.password && (
                          <p className="text-sm text-destructive">{registerForm.formState.errors.password.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirmer *</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          className="h-11 bg-slate-50 dark:bg-slate-800 text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          {...registerForm.register("confirmPassword")}
                        />
                        {registerForm.formState.errors.confirmPassword && (
                          <p className="text-sm text-destructive">{registerForm.formState.errors.confirmPassword.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Marketing Opt-in */}
                    <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="accept_marketing"
                          checked={registerForm.watch("accept_marketing")}
                          onCheckedChange={(checked) => registerForm.setValue("accept_marketing", checked)}
                        />
                        <label htmlFor="accept_marketing" className="text-sm leading-tight cursor-pointer">
                          <span className="font-medium text-amber-800">Recevoir des offres exclusives</span>
                          <p className="text-amber-700 text-xs mt-1">
                            Je souhaite être informé(e) des nouveaux jeux, tirages au sort et offres des partenaires.
                          </p>
                        </label>
                      </div>
                    </div>

                    {/* Terms */}
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="accept_terms"
                        checked={registerForm.watch("accept_terms")}
                        onCheckedChange={(checked) => registerForm.setValue("accept_terms", checked)}
                      />
                      <label htmlFor="accept_terms" className="text-sm text-muted-foreground cursor-pointer">
                        J'accepte les <Link to="/terms" className="text-primary underline">conditions d'utilisation</Link> et la <Link to="/privacy" className="text-primary underline">politique de confidentialité</Link>
                      </label>
                    </div>
                    {registerForm.formState.errors.accept_terms && (
                      <p className="text-sm text-destructive">{registerForm.formState.errors.accept_terms.message}</p>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full h-11 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer mon compte"}
                    </Button>
                  </form>
                )}

                <div className="text-center mt-6">
                  <p className="text-sm text-muted-foreground">
                    {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}
                    <button 
                      onClick={() => setMode(mode === "login" ? "register" : "login")}
                      className="text-primary font-medium ml-1 hover:underline"
                    >
                      {mode === "login" ? "S'inscrire" : "Se connecter"}
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Unsubscribe info */}
            <p className="text-center text-white/60 text-xs mt-6">
              Vous pouvez vous désabonner à tout moment sur votre profil ou en écrivant à{" "}
              <a href="mailto:stop@tireo.app" className="underline">stop@tireo.app</a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
